import React, { useEffect, useState } from 'react';
import { Button, Input, message, Modal, Form, Upload, Card, List, Popconfirm, Spin } from 'antd';
import { PlusOutlined, UploadOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import drEasyApi from '../../../Api/Customapi';
import ImageUploader from '../../Ui/ImageUploader';
import { getImg } from '../../../Api/getImg';
import { MdHomeRepairService } from 'react-icons/md';

const BlogScreen = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [editingBlog, setEditingBlog] = useState(null);
    const [viewingBlog, setViewingBlog] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchBlog();
    }, []);

    const fetchBlog = async () => {
        setLoading(true);
        try {
            const res = await drEasyApi.get('/api/Blogs/GetMyBlogs');
            setBlogs(res.data || []);
        } catch (err) {
            if (err.response.status == 403) {
                window.location.href = "/sub"; // Adjust auth path as needed
            }        
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingBlog(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (blog) => {
        setEditingBlog(blog);
        form.setFieldsValue(blog);
        setIsModalVisible(true);
    };

    const handleViewDetails = (blog) => {
        setViewingBlog(blog);
        setIsViewModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await drEasyApi.delete(`/api/Blogs/Delete`, { params: { id } });
            message.success('Blog deleted');
            fetchBlog();
        } catch (err) {
            message.error('Failed to delete blog');
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (editingBlog) {
                await drEasyApi.put('/api/Blogs/Edit', { ...values, id: editingBlog.id });
                message.success('Blog updated');
            } else {
                await drEasyApi.post('/api/Blogs/Add', values);
                message.success('Blog added');
            }
            fetchBlog();
            setIsModalVisible(false);
        } catch (err) {
            if (err?.response?.status === 400) {
                message.error('Please fill all fields correctly');
            } else {
                message.error('Failed to submit blog');
            }
        }
    };

    const truncateDescription = (description, maxLength = 300) => {
        if (!description || description.length <= maxLength) {
            return description;
        }
        return description.substring(0, maxLength) + '...';
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold"> Blogs</h2>
                <Button type="primary" className='bg-[#298d9f]' icon={<PlusOutlined />} onClick={handleAdd}>Add Blog</Button>
            </div>

            {loading ? (
                <div className="flex justify-center items-center">
                    <Spin size="large" />
                </div>
            ) : (
                <List
                    grid={{
                        gutter: 16,
                        xs: 1,
                        sm: 1,
                        md: 2,
                        lg: 2,
                        xl: 3,
                        xxl: 3,
                    }}
                    dataSource={blogs}
                    renderItem={(item) => (
                        <List.Item>
                            <Card
                                className="overflow-hidden shadow-md border border-[#298d9f73] hover:shadow-lg transition-all rounded-lg"
                                cover={
                                    item.photoID ? (
                                        <div className="bg-[#f6fcfd] p-2 flex justify-center items-center h-60">
                                            <img
                                                src={getImg(item.photoID)}
                                                alt="Blog"
                                                className="max-h-full w-full object-contain rounded"
                                            />
                                        </div>
                                    ) : null
                                }
                                actions={[
                                    <Button
                                        icon={<EditOutlined />}
                                        onClick={() => handleEdit(item)}
                                        type="text"
                                        key="edit"
                                    />,
                                    <Popconfirm
                                        title="Are you sure to delete this Blog?"
                                        onConfirm={() => handleDelete(item.id)}
                                        key="delete"
                                    >
                                        <Button danger icon={<DeleteOutlined />} type="text" />
                                    </Popconfirm>,
                                ]}
                            >
                                <Card.Meta
                                    title={
                                        <div className="flex items-center gap-2 text-lg font-semibold text-[#298d9f]">
                                            {item.title}
                                        </div>
                                    }
                                    description={
                                        <div className=' '>
                                            <p className="text-sm text-gray-600">
                                                {truncateDescription(item.description)}
                                            {item.description && item.description.length > 300 && (
                                                <Button 
                                                    type="link" 
                                                    size="small" 
                                                    onClick={() => handleViewDetails(item)}
                                                    className="p-0 underline text-[#298d9f] hover:text-[#1f6b7a]"
                                                >
                                                    See full blog
                                                </Button>
                                            )}
                                            </p>
                                        </div>
                                    }
                                />
                            </Card>
                        </List.Item>
                    )}
                />
            )}

            {/* Add/Edit Modal */}
            <Modal
                title={editingBlog ? 'Edit Blog' : 'Add Blog'}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setIsModalVisible(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        onClick={handleSubmit}
                        style={{ backgroundColor: '#298d9f', borderColor: '#298d9f' }}
                    >
                        Save
                    </Button>,
                ]}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Please enter title' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="titleAr" label="Title (In Arabic)" rules={[{ required: true, message: 'Please enter title' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="description" label="Description" rules={[{ required: true, message: 'Please enter description' }]}>
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item name="descriptionAr" label="Description (In Arabic)" rules={[{ required: true, message: 'Please enter description' }]}>
                        <Input.TextArea rows={3} />
                    </Form.Item>
                   
                    <Form.Item name="photoID" label="Blog Image" rules={[{ required: true, message: 'Please Add image' }]}>
                        <ImageUploader form={form} value={form.getFieldValue("photoID")} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* View Details Modal */}
            <Modal
                title="Blog Details"
                open={isViewModalVisible}
                onCancel={() => setIsViewModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsViewModalVisible(false)}>
                        Close
                    </Button>,
                ]}
                width={700}
                destroyOnClose
            >
                {viewingBlog && (
                    <div>
                        <h3 className="text-xl font-semibold text-[#298d9f] mb-4">
                            {viewingBlog.title}
                        </h3>
                        
                        {viewingBlog.photoID && (
                            <div className="mb-4 flex justify-center">
                                <img
                                    src={getImg(viewingBlog.photoID)}
                                    alt="Blog"
                                    className="max-w-full max-h-80 object-contain rounded-lg border"
                                />
                            </div>
                        )}
                        
                        <div className="text-gray-700">
                            <h4 className="font-medium mb-2">Description:</h4>
                            <p className="text-sm leading-relaxed whitespace-pre-wrap">
                                {viewingBlog.description}
                            </p>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default BlogScreen;